/**
 * Marketplace Data Source — Single Source of Truth (Feb 2026)
 *
 * Authoritative store for marketplace metadata across the entire Zaroori
 * Retail frontend. Every hub card, comparison page, SEO page, FAQ generator,
 * recommendation widget, and entity card MUST read structured fields from
 * here. Hardcoding marketplace timelines / difficulty / startup-friendliness
 * anywhere else fails `marketplace-data-audit.js`.
 *
 * Schema (per marketplace):
 *   name                  — Display name
 *   slug                  — Lowercase URL-safe identifier
 *   timeline              — Typical brand approval timeline (e.g. "10-15 days")
 *   startupFriendly       — "Yes" | "Moderate" | "No"
 *   approvalDifficulty    — "Low" | "Medium" | "High"
 *   growthPotential       — "Low" | "Medium" | "High" | "Very High"
 *   amComplexity          — "Low" | "Medium" | "High"
 *   categoryFocus         — Comma-separated category labels
 *   recommendedBrandType  — One-line ideal-customer description (LLM/GEO fuel)
 *   audience              — Marketplace consumer base summary
 *   aovBand               — Typical AOV range
 *   region                — "India" | "UAE" | "GCC"
 *   serviceUrls           — { registration, brandApproval, accountManagement }
 *   comparisonUrls        — Array of /<slug>-vs-<other> comparison routes
 *
 * Backwards-compatibility: legacy callsites importing `MARKETPLACE_DATA` from
 * sprint4ComparisonPages.js continue to work — that module now re-exports
 * from this file.
 */

export const MARKETPLACES = {
  myntra: {
    name: 'Myntra',
    slug: 'myntra',
    region: 'India',
    timeline: '10-15 days',
    startupFriendly: 'Yes',
    approvalDifficulty: 'Medium',
    growthPotential: 'Very High',
    amComplexity: 'Medium',
    categoryFocus: 'Fashion, Apparel, Footwear, Accessories',
    recommendedBrandType:
      'D2C and bridge-premium fashion brands with 20+ hero SKUs, Myntra-spec photography, and an active or pending Class 25 trademark.',
    audience: 'Mainstream + premium fashion, 70M+ MAU',
    aovBand: '₹1,650-2,400',
    serviceUrls: {
      registration: '/myntra-seller-registration',
      brandApproval: '/myntra-brand-approval-india',
      accountManagement: '/myntra-seller-account-management',
    },
    comparisonUrls: ['/myntra-vs-nykaa', '/myntra-vs-tira'],
  },

  nykaa: {
    name: 'Nykaa',
    slug: 'nykaa',
    region: 'India',
    timeline: '20-25 days',
    startupFriendly: 'Yes',
    approvalDifficulty: 'High',
    growthPotential: 'Very High',
    amComplexity: 'High',
    categoryFocus: 'Beauty, Cosmetics, Personal Care, Wellness',
    recommendedBrandType:
      'Beauty / personal care brands with FSSAI, ingredient story, content-led brand book, and demonstrable D2C/Instagram traction.',
    audience: 'Curated beauty consumer + Pink Pass members, 30M+ registered users',
    aovBand: '₹950-1,800 (Beauty) | ₹2,200-3,800 (Fashion)',
    serviceUrls: {
      registration: '/nykaa-seller-registration',
      brandApproval: '/nykaa-brand-approval',
      accountManagement: null,
    },
    comparisonUrls: ['/myntra-vs-nykaa', '/nykaa-vs-tira'],
  },

  tira: {
    name: 'Tira',
    slug: 'tira',
    region: 'India',
    timeline: '30-35 days',
    startupFriendly: 'Moderate',
    approvalDifficulty: 'High',
    growthPotential: 'High',
    amComplexity: 'Medium',
    categoryFocus: 'Beauty, Cosmetics, Personal Care',
    recommendedBrandType:
      'Genuinely premium beauty brands with omnichannel readiness (Tira.com + app + 100+ flagship stores) and FSSAI/CDSCO compliance.',
    audience: 'UHNW + HNW premium beauty consumer, Tira.com + app + 100+ flagship stores',
    aovBand: '₹2,400-4,800',
    serviceUrls: {
      registration: null,
      brandApproval: '/tira-brand-approval',
      accountManagement: null,
    },
    comparisonUrls: ['/myntra-vs-tira', '/nykaa-vs-tira'],
  },

  ajio: {
    name: 'AJIO',
    slug: 'ajio',
    region: 'India',
    timeline: '16-26 days',
    startupFriendly: 'Yes',
    approvalDifficulty: 'Medium',
    growthPotential: 'Very High',
    amComplexity: 'Medium',
    categoryFocus: 'Fashion, Footwear, Accessories, Lifestyle',
    recommendedBrandType:
      'Fashion brands across mainstream-to-premium with AOV-aligned positioning for Brand Mall (₹399+), AJIO Luxe (₹3,500+), or AJIO Indie (₹1,200+).',
    audience: 'Tier-2/3 mainstream + AJIO Luxe premium consumer, 35-45% YoY growth',
    aovBand: '₹599-1,400 (Brand Mall) | ₹3,800-8,400 (Luxe)',
    serviceUrls: {
      registration: '/ajio-seller-registration',
      brandApproval: null,
      accountManagement: null,
    },
    comparisonUrls: [],
  },

  firstcry: {
    name: 'FirstCry',
    slug: 'firstcry',
    region: 'India',
    timeline: '18-28 days',
    startupFriendly: 'Moderate',
    approvalDifficulty: 'High',
    growthPotential: 'Very High',
    amComplexity: 'High',
    categoryFocus: 'Baby Care, Kids Apparel, Toys, Mom Care, Infant Nutrition',
    recommendedBrandType:
      'Baby / mom / kids brands with BIS (toys), FSSAI (nutrition), materials disclosure, and NABL-accredited lab-test reports.',
    audience: 'New parents + maternity consumer cohort, ₹38K+ LTV over 3 years',
    aovBand: '₹2,400-3,800',
    serviceUrls: {
      registration: '/firstcry-seller-registration',
      brandApproval: null,
      accountManagement: null,
    },
    comparisonUrls: [],
  },

  bigbasket: {
    name: 'BigBasket',
    slug: 'bigbasket',
    region: 'India',
    timeline: '20-32 days',
    startupFriendly: 'Moderate',
    approvalDifficulty: 'Medium',
    growthPotential: 'Very High',
    amComplexity: 'High',
    categoryFocus: 'Staples, Packaged Foods, Beverages, Personal Care, Gourmet',
    recommendedBrandType:
      'Grocery / FMCG / wellness brands with FSSAI compliance, cold-chain readiness (where relevant), and BB Now Quick Commerce SKU velocity.',
    audience: '18M+ MAU, Tata Neu + Croma cross-promotion network',
    aovBand: '₹1,200+ (standard BB) | premium on BB Now',
    serviceUrls: {
      registration: '/bigbasket-seller-registration',
      brandApproval: null,
      accountManagement: null,
    },
    comparisonUrls: [],
  },

  blinkit: {
    name: 'Blinkit',
    slug: 'blinkit',
    region: 'India',
    timeline: '7-12 days',
    startupFriendly: 'Yes',
    approvalDifficulty: 'Medium',
    growthPotential: 'Very High',
    amComplexity: 'High',
    categoryFocus: 'Grocery, FMCG, Quick Commerce, Personal Care',
    recommendedBrandType:
      'Brands with high SKU velocity, dark-store-friendly packaging, FSSAI compliance, and 30-minute Quick Commerce category fit.',
    audience: '10-min Quick Commerce consumer cohort, urban tier-1/2',
    aovBand: '₹350-650',
    serviceUrls: {
      registration: '/blinkit-seller-registration',
      brandApproval: null,
      accountManagement: '/blinkit-account-management',
    },
    comparisonUrls: [],
  },

  zepto: {
    name: 'Zepto',
    slug: 'zepto',
    region: 'India',
    timeline: '7-12 days',
    startupFriendly: 'Yes',
    approvalDifficulty: 'Medium',
    growthPotential: 'Very High',
    amComplexity: 'High',
    categoryFocus: 'Grocery, FMCG, Quick Commerce, Personal Care, Beauty',
    recommendedBrandType:
      'Quick Commerce SKUs with high impulse-purchase velocity, dark-store picking efficiency, and aggressive PLA appetite.',
    audience: '10-min Quick Commerce + Zepto Cafe consumer, urban tier-1',
    aovBand: '₹380-720',
    serviceUrls: {
      registration: '/zepto-seller-registration',
      brandApproval: null,
      accountManagement: '/zepto-account-management',
    },
    comparisonUrls: [],
  },

  instamart: {
    name: 'Swiggy Instamart',
    slug: 'instamart',
    region: 'India',
    timeline: '8-14 days',
    startupFriendly: 'Yes',
    approvalDifficulty: 'Medium',
    growthPotential: 'Very High',
    amComplexity: 'High',
    categoryFocus: 'Grocery, FMCG, Quick Commerce, Personal Care',
    recommendedBrandType:
      'High-velocity SKUs aligned to Swiggy ecosystem (food + grocery cross-sell) with margin discipline for Instamart pricing structure.',
    audience: 'Swiggy Instamart consumer (food + grocery overlap), urban tier-1/2',
    aovBand: '₹320-580',
    serviceUrls: {
      registration: '/swiggy-instamart-seller-registration',
      brandApproval: null,
      accountManagement: '/swiggy-instamart-account-management',
    },
    comparisonUrls: [],
  },

  noon: {
    name: 'Noon',
    slug: 'noon',
    region: 'UAE',
    timeline: '22-35 days',
    startupFriendly: 'Moderate',
    approvalDifficulty: 'High',
    growthPotential: 'Very High',
    amComplexity: 'High',
    categoryFocus: 'Multi-category (Fashion, Beauty, Foodhall, Noon Now)',
    recommendedBrandType:
      'Brands ready for UAE trade license, VAT, Arabic content for KSA/Egypt, and three-country GCC fulfilment via Noon cross-border logistics.',
    audience: '28M+ MAU across UAE / KSA / Egypt, highest AOV in regional eCommerce',
    aovBand: 'AED 240-680',
    serviceUrls: {
      registration: '/noon-seller-registration-uae',
      brandApproval: null,
      accountManagement: null,
    },
    comparisonUrls: [],
  },

  namshi: {
    name: 'Namshi',
    slug: 'namshi',
    region: 'UAE',
    timeline: '22-32 days',
    startupFriendly: 'Moderate',
    approvalDifficulty: 'High',
    growthPotential: 'High',
    amComplexity: 'High',
    categoryFocus: 'Premium Fashion, Footwear, Accessories, Beauty',
    recommendedBrandType:
      'Premium fashion brands with GCC trademark, Middle East-aesthetic lookbook, Arabic content for KSA shelf, and AED 380-1,200 AOV positioning.',
    audience: 'Premium-to-luxury fashion consumer (UAE + KSA), Al Tayer Group consumer base',
    aovBand: 'AED 380-1,200',
    serviceUrls: {
      registration: '/namshi-seller-registration-uae',
      brandApproval: null,
      accountManagement: null,
    },
    comparisonUrls: [],
  },

  ounass: {
    name: 'Ounass',
    slug: 'ounass',
    region: 'GCC',
    timeline: '28-42 days',
    startupFriendly: 'No',
    approvalDifficulty: 'High',
    growthPotential: 'High',
    amComplexity: 'Medium',
    categoryFocus: 'Luxury Fashion, Jewellery, Watches, Beauty, Home',
    recommendedBrandType:
      'Genuinely luxury brands (AED 1,200-4,800 AOV) with demonstrable heritage, GCC trademark, premium content readiness, and concession-model commercial alignment.',
    audience: 'UHNW + HNW GCC consumer across UAE, KSA, Kuwait, Bahrain, Oman, Qatar',
    aovBand: 'AED 1,200-4,800',
    serviceUrls: {
      registration: '/ounass-seller-registration-uae',
      brandApproval: null,
      accountManagement: null,
    },
    comparisonUrls: [],
  },
};

/** Lookup helpers (zero-mutation). */
export const getMarketplace = (slug) => MARKETPLACES[slug] || null;
export const listMarketplaces = () => Object.values(MARKETPLACES);
export const listByRegion = (region) =>
  Object.values(MARKETPLACES).filter((m) => m.region === region);

/** Map a marketplace's slug to a Sprint 4 SEO page (priority: brand approval > registration > AM). */
export const primaryServiceUrl = (slug) => {
  const m = MARKETPLACES[slug];
  if (!m) return null;
  return m.serviceUrls.brandApproval || m.serviceUrls.registration || m.serviceUrls.accountManagement || null;
};

/**
 * PRIORITY_SEO_MARKETPLACES — Jun 2026 SEO/AI scope lock (SSOT).
 *
 * The ONLY marketplaces that receive new SEO / AI-visibility investment.
 * Mirrored by backend/routes/marketplace_seo.py → MARKETPLACE_REGISTRY;
 * tests/test_marketplace_seo_dashboard.py asserts both stay in sync.
 *
 *   tier: 'P0' build first · 'P1' next · 'P2' after · 'maintenance' monitor only
 *         · 'platform' software entity (not a marketplace)
 *   contentPolicy: 'commercial' | 'educational-only' (Amazon compliance)
 */
export const PRIORITY_SEO_MARKETPLACES = [
  { slug: 'myntra', name: 'Myntra', tier: 'P0', rank: 1, hubUrl: '/myntra', cluster: 'myntra', contentPolicy: 'commercial' },
  { slug: 'nykaa', name: 'Nykaa', tier: 'P0', rank: 2, hubUrl: '/services/nykaa', cluster: 'nykaa', contentPolicy: 'commercial' },
  { slug: 'blinkit', name: 'Blinkit', tier: 'P0', rank: 3, hubUrl: '/services/blinkit', cluster: 'blinkit', contentPolicy: 'commercial' },
  { slug: 'zepto', name: 'Zepto', tier: 'P0', rank: 4, hubUrl: '/services/zepto', cluster: 'zepto', contentPolicy: 'commercial' },
  { slug: 'ajio', name: 'AJIO', tier: 'P0', rank: 5, hubUrl: '/services/ajio', cluster: 'ajio', contentPolicy: 'commercial' },
  { slug: 'tata-cliq', name: 'Tata CLiQ', tier: 'P1', rank: 6, hubUrl: '/services/tata-cliq', cluster: 'tata-cliq', contentPolicy: 'commercial' },
  { slug: 'swiggy-instamart', name: 'Swiggy Instamart', tier: 'P1', rank: 7, hubUrl: '/services/swiggy-instamart', cluster: 'swiggy-instamart', contentPolicy: 'commercial' },
  { slug: 'pernias-pop-up-shop', name: "Pernia's Pop-Up Shop", tier: 'P2', rank: 8, hubUrl: '/services/pernias-pop-up-shop', cluster: 'pernia', contentPolicy: 'commercial' },
  { slug: 'aza-fashions', name: 'Aza Fashions', tier: 'P2', rank: 9, hubUrl: '/services/aza-fashions', cluster: 'aza-fashions', contentPolicy: 'commercial' },
  { slug: 'amazon', name: 'Amazon', tier: 'P2', rank: 10, hubUrl: '/amazon-seller-onboarding-india', cluster: 'amazon', contentPolicy: 'educational-only' },
  { slug: 'tira', name: 'Tira', tier: 'maintenance', rank: 20, hubUrl: '/services/tira', cluster: 'tira', contentPolicy: 'commercial' },
  { slug: 'bigbasket', name: 'BigBasket', tier: 'maintenance', rank: 21, hubUrl: '/services/bigbasket', cluster: 'bigbasket', contentPolicy: 'commercial' },
  { slug: 'zaroori-jini-oms', name: 'Zaroori Jini OMS', tier: 'platform', rank: 30, hubUrl: '/zaroori-jini-oms', cluster: 'zaroori-jini-oms', contentPolicy: 'commercial' },
];

export const PRIORITY_SEO_SLUGS = PRIORITY_SEO_MARKETPLACES
  .filter((m) => ['P0', 'P1', 'P2'].includes(m.tier))
  .map((m) => m.slug);

export const getPrioritySeoMarketplace = (slug) =>
  PRIORITY_SEO_MARKETPLACES.find((m) => m.slug === slug) || null;

export default MARKETPLACES;
